import { Injectable } from '@angular/core';

/** Key de localStorage para validaciones ADRES + Historial del beneficiario (multi-documento). */
export const KEY_VALIDACIONES_BENEFICIARIO = 'validaciones_beneficiario';
const KEY_VALIDACIONES_CONTRATANTE = 'validaciones_contratante';
const KEY_COTIZACION_PRECARGADA = 'cotizacion_precargada';

/** Misma forma de ítem que validaciones_contratante (sin Condición asociado). */
export interface ItemValidacionBeneficiario {
  fuente: string;
  descripcion: string;
  fechaEvento: string;
  tipo?: string;
}

/** Una entrada por documento consultado. */
export interface ValidacionBeneficiarioEntry {
  tipoDocumento: string;
  idBeneficiario: string;
  fechaConsulta: string;
  items: ItemValidacionBeneficiario[];
}

/** Vista lista para pintar en el tab Resultado búsqueda. */
export interface ValidacionesBeneficiarioUi {
  estadoEPS: { tipo: string; mensaje: string }[];
  historialCliente: { tipo: string; mensaje: string }[];
}

const FUENTE_ADRES = 'ADRES';
const FUENTE_HISTORIAL = 'Historial Afilmed';
const MESES_VALIDEZ_CONSULTA = 6;

/**
 * Borra validaciones_beneficiario solo si la carga actual es una recarga del navegador.
 * No afecta cierre de diálogo, consulta de otro documento ni navegación interna de la app.
 */
export function limpiarValidacionesBeneficiarioSiRecargaNavegador(): void {
  try {
    const nav = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
    if (nav?.type === 'reload') {
      localStorage.removeItem(KEY_VALIDACIONES_BENEFICIARIO);
      return;
    }
    // Fallback navegadores antiguos: TYPE_RELOAD === 1
    const legacy = (performance as any)?.navigation;
    if (legacy && legacy.type === 1) {
      localStorage.removeItem(KEY_VALIDACIONES_BENEFICIARIO);
    }
  } catch {
    // ignore
  }
}

@Injectable({ providedIn: 'root' })
export class ValidacionesBeneficiarioLocalStorageService {

  constructor() {
    this.limpiarSiRecargaNavegador();
  }

  /** Borra el cache multi-documento del beneficiario. */
  limpiar(): void {
    try {
      localStorage.removeItem(KEY_VALIDACIONES_BENEFICIARIO);
    } catch {
      // ignore
    }
  }

  private limpiarSiRecargaNavegador(): void {
    limpiarValidacionesBeneficiarioSiRecargaNavegador();
  }

  /**
   * Busca cache de beneficiario; si no hay, reutiliza validaciones_contratante
   * cuando tipo+documento coinciden. Si viene del contratante, la copia al array del beneficiario.
   */
  obtenerVistaParaConsulta(tipoDocumento: string, numeroDocumento: string): ValidacionesBeneficiarioUi | null {
    const tipoDoc = (tipoDocumento || '').trim();
    const numDoc = (numeroDocumento || '').trim();
    if (!tipoDoc || !numDoc) {
      return null;
    }

    const entryBenef = this.buscarPorDocumento(tipoDoc, numDoc);
    if (entryBenef && this.esVigente(entryBenef)) {
      return this.mapearItemsAUi(entryBenef.items);
    }

    const entryDesdeContratante = this.intentarDesdeContratante(tipoDoc, numDoc);
    if (entryDesdeContratante) {
      this.upsert(entryDesdeContratante);
      return this.mapearItemsAUi(entryDesdeContratante.items);
    }

    return null;
  }

  /** Persiste (o actualiza) la consulta actual sin eliminar otras entradas. */
  guardarConsulta(
    tipoDocumento: string,
    numeroDocumento: string,
    estadoEPS: { tipo?: string; mensaje: string }[],
    historialCliente: { tipo?: string; mensaje: string }[],
    fechaEventoAdres: string = ''
  ): void {
    const tipoDoc = (tipoDocumento || '').trim();
    const numDoc = (numeroDocumento || '').trim();
    if (!tipoDoc || !numDoc) {
      return;
    }

    const items = this.construirItemsDesdeUi(estadoEPS, historialCliente, fechaEventoAdres);
    this.upsert({
      tipoDocumento: tipoDoc,
      idBeneficiario: numDoc,
      fechaConsulta: new Date().toISOString(),
      items
    });
  }

  obtenerTodas(): ValidacionBeneficiarioEntry[] {
    try {
      const raw = localStorage.getItem(KEY_VALIDACIONES_BENEFICIARIO);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  buscarPorDocumento(tipoDocumento: string, numeroDocumento: string): ValidacionBeneficiarioEntry | null {
    const tipoDoc = (tipoDocumento || '').trim();
    const numDoc = (numeroDocumento || '').trim();
    const todas = this.obtenerTodas();
    return todas.find(e =>
      (e?.tipoDocumento || '').trim() === tipoDoc &&
      (e?.idBeneficiario || '').trim() === numDoc
    ) ?? null;
  }

  upsert(entry: ValidacionBeneficiarioEntry): void {
    if (!entry?.tipoDocumento || !entry?.idBeneficiario) {
      return;
    }
    const todas = this.obtenerTodas();
    const idx = todas.findIndex(e =>
      (e?.tipoDocumento || '').trim() === entry.tipoDocumento.trim() &&
      (e?.idBeneficiario || '').trim() === entry.idBeneficiario.trim()
    );
    if (idx >= 0) {
      todas[idx] = entry;
    } else {
      todas.push(entry);
    }
    try {
      localStorage.setItem(KEY_VALIDACIONES_BENEFICIARIO, JSON.stringify(todas));
    } catch {
      // ignore
    }
  }

  mapearItemsAUi(items: ItemValidacionBeneficiario[]): ValidacionesBeneficiarioUi {
    const list = Array.isArray(items) ? items : [];
    const estadoEPS = list
      .filter(i => {
        const fuente = (i?.fuente || '').trim();
        const desc = (i?.descripcion || '').trim();
        return fuente === FUENTE_ADRES &&
          desc !== 'No se pudo completar la consulta de EPS.' &&
          !desc.includes('Error en el servicio');
      })
      .map(i => ({ tipo: i.tipo || 'success', mensaje: i.descripcion }));

    const historialCliente = list
      .filter(i => (i?.fuente || '').trim() === FUENTE_HISTORIAL)
      .map(i => ({ tipo: i.tipo || 'info', mensaje: i.descripcion }));

    return { estadoEPS, historialCliente };
  }

  construirItemsDesdeUi(
    estadoEPS: { tipo?: string; mensaje: string }[],
    historialCliente: { tipo?: string; mensaje: string }[],
    fechaEventoAdres: string = ''
  ): ItemValidacionBeneficiario[] {
    const items: ItemValidacionBeneficiario[] = [];
    (estadoEPS || []).forEach(t => {
      const desc = (t?.mensaje || '').trim();
      if (!desc) {
        return;
      }
      items.push({
        fuente: FUENTE_ADRES,
        descripcion: desc,
        fechaEvento: fechaEventoAdres || '',
        tipo: t.tipo || 'success'
      });
    });
    (historialCliente || []).forEach(t => {
      const desc = (t?.mensaje || '').trim();
      if (!desc) {
        return;
      }
      items.push({
        fuente: FUENTE_HISTORIAL,
        descripcion: desc,
        fechaEvento: '',
        tipo: t.tipo || 'info'
      });
    });
    return items;
  }

  private esVigente(entry: ValidacionBeneficiarioEntry): boolean {
    if (!entry?.fechaConsulta || !Array.isArray(entry.items)) {
      return false;
    }
    if (this.esEditModeActivo()) {
      return true;
    }
    const fechaConsulta = new Date(entry.fechaConsulta);
    if (Number.isNaN(fechaConsulta.getTime())) {
      return false;
    }
    const haceMeses = new Date();
    haceMeses.setMonth(haceMeses.getMonth() - MESES_VALIDEZ_CONSULTA);
    return fechaConsulta >= haceMeses;
  }

  private esEditModeActivo(): boolean {
    try {
      const raw = localStorage.getItem(KEY_COTIZACION_PRECARGADA);
      return raw ? JSON.parse(raw)?.editMode === true : false;
    } catch {
      return false;
    }
  }

  /**
   * Si el documento del beneficiario coincide con el contratante validado,
   * reutiliza solo ADRES + Historial Afilmed (sin Condición asociado).
   */
  private intentarDesdeContratante(tipoDoc: string, numDoc: string): ValidacionBeneficiarioEntry | null {
    try {
      const raw = localStorage.getItem(KEY_VALIDACIONES_CONTRATANTE);
      if (!raw) {
        return null;
      }
      const stored = JSON.parse(raw) as {
        tipoDocumento?: string;
        idContratante?: string;
        fechaConsulta?: string;
        items?: ItemValidacionBeneficiario[];
      };
      if (!Array.isArray(stored?.items) || !stored.items.length) {
        return null;
      }
      if ((stored.tipoDocumento || '').trim() !== tipoDoc || (stored.idContratante || '').trim() !== numDoc) {
        return null;
      }
      if (!stored.fechaConsulta) {
        return null;
      }

      const fechaConsulta = new Date(stored.fechaConsulta);
      if (Number.isNaN(fechaConsulta.getTime())) {
        return null;
      }
      if (!this.esEditModeActivo()) {
        const haceMeses = new Date();
        haceMeses.setMonth(haceMeses.getMonth() - MESES_VALIDEZ_CONSULTA);
        if (fechaConsulta < haceMeses) {
          return null;
        }
      }

      const items = stored.items.filter(i => {
        const fuente = (i?.fuente || '').trim();
        return fuente === FUENTE_ADRES || fuente === FUENTE_HISTORIAL;
      });
      if (!items.length) {
        return null;
      }

      return {
        tipoDocumento: tipoDoc,
        idBeneficiario: numDoc,
        fechaConsulta: stored.fechaConsulta,
        items
      };
    } catch {
      return null;
    }
  }
}
